import React, { useRef, useEffect, useState, useCallback } from 'react';

const BLOCK_COLORS = {
    paragraph_title: { fill: 'rgba(47,109,246,0.12)', border: '#506BF8' },
    doc_title: { fill: 'rgba(47,109,246,0.12)', border: '#506BF8' },
    text: { fill: 'rgba(47,109,246,0.08)', border: '#506BF8' },
    table: { fill: 'rgba(56,178,96,0.18)', border: '#2FBD71' },
    display_formula: { fill: 'rgba(212,177,6,0.18)', border: '#fadb14' },
    inline_formula: { fill: 'rgba(212,177,6,0.18)', border: '#fadb14' },
    image: { fill: 'rgba(155,89,182,0.18)', border: '#BD4CFF' },
    chart: { fill: 'rgba(155,89,182,0.18)', border: '#BD4CFF' },
    seal: { fill: 'rgba(235,87,87,0.18)', border: '#F94E28' },
    algorithm: { fill: 'rgba(235,87,87,0.18)', border: '#F94E28' },
};
const DEFAULT_COLOR = { fill: 'rgba(47,109,246,0.08)', border: '#506BF8' };
const HOVER_BORDER = '#fadb14';
const SELECTED_BORDER = '#2E77FF';
const LABEL_NAMES = {
    paragraph_title: 'Title', doc_title: 'Doc title', text: 'Text', table: 'Table',
    display_formula: 'Display formula', inline_formula: 'Inline formula',
    image: 'Image', chart: 'Chart', seal: 'Seal', algorithm: 'Algorithm',
    header: 'Header', footer: 'Footer', header_image: 'Header image',
    footer_image: 'Footer image', number: 'Page number', footnote: 'Footnote', aside_text: 'Aside text',
};

function getBlockBounds(block) {
    if (block.block_polygon_points?.length >= 4) {
        const pts = block.block_polygon_points;
        return {
            x: Math.min(...pts.map(p => p[0])), y: Math.min(...pts.map(p => p[1])),
            w: Math.max(...pts.map(p => p[0])) - Math.min(...pts.map(p => p[0])),
            h: Math.max(...pts.map(p => p[1])) - Math.min(...pts.map(p => p[1])),
        };
    }
    if (block.block_bbox) {
        const [x1, y1, x2, y2] = block.block_bbox;
        return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
    }
    return null;
}

function drawOverlay(ctx, blocks, scale, hoveredIdx, selectedIdx) {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillRect(0, 0, w, h);

    for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        const bounds = getBlockBounds(block);
        if (!bounds) continue;
        const sx = bounds.x * scale, sy = bounds.y * scale, sw = bounds.w * scale, sh = bounds.h * scale;

        ctx.save();
        ctx.beginPath();
        if (block.block_polygon_points?.length >= 4) {
            const pts = block.block_polygon_points;
            ctx.moveTo(pts[0][0] * scale, pts[0][1] * scale);
            for (let j = 1; j < pts.length; j++) ctx.lineTo(pts[j][0] * scale, pts[j][1] * scale);
            ctx.closePath();
        } else {
            ctx.rect(sx, sy, sw, sh);
        }
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fill();
        ctx.restore();

        const color = BLOCK_COLORS[block.block_label] || DEFAULT_COLOR;
        const isHovered = i === hoveredIdx;
        const isSelected = i === selectedIdx;

        ctx.save();
        ctx.beginPath();
        if (block.block_polygon_points?.length >= 4) {
            const pts = block.block_polygon_points;
            ctx.moveTo(pts[0][0] * scale, pts[0][1] * scale);
            for (let j = 1; j < pts.length; j++) ctx.lineTo(pts[j][0] * scale, pts[j][1] * scale);
            ctx.closePath();
        } else {
            ctx.rect(sx, sy, sw, sh);
        }

        if (isSelected) {
            ctx.strokeStyle = SELECTED_BORDER; ctx.lineWidth = 3;
            ctx.fillStyle = 'rgba(46,119,255,0.08)'; ctx.fill();
        } else if (isHovered) {
            ctx.strokeStyle = HOVER_BORDER; ctx.lineWidth = 3;
            ctx.fillStyle = color.fill; ctx.fill();
        } else {
            ctx.strokeStyle = color.border; ctx.lineWidth = 1.5;
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.stroke();
        ctx.restore();

        if (isHovered || isSelected) {
            const label = LABEL_NAMES[block.block_label] || block.block_label;
            const borderColor = isSelected ? SELECTED_BORDER : HOVER_BORDER;
            const lx = bounds.x * scale;
            const ly = bounds.y * scale - 20;
            ctx.save();
            ctx.font = '11px -apple-system, sans-serif';
            const tw = ctx.measureText(label).width + 10;
            ctx.fillStyle = borderColor;
            ctx.beginPath();
            ctx.roundRect(lx, Math.max(0, ly), tw, 18, 3);
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.fillText(label, lx + 5, Math.max(0, ly) + 13);
            ctx.restore();
        }
    }
}

function drawSpottingOnSource(ctx, polys, texts, scale, hoveredIdx, selectedIdx) {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);

    for (let i = 0; i < polys.length; i++) {
        const poly = polys[i];
        if (!poly || poly.length < 4) continue;
        const isHovered = i === hoveredIdx;
        const isSelected = i === selectedIdx;
        if (!isHovered && !isSelected) continue;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(poly[0][0] * scale, poly[0][1] * scale);
        for (let j = 1; j < poly.length; j++) {
            ctx.lineTo(poly[j][0] * scale, poly[j][1] * scale);
        }
        ctx.closePath();

        ctx.strokeStyle = isSelected ? SELECTED_BORDER : HOVER_BORDER;
        ctx.lineWidth = 2;
        ctx.stroke();

        const text = texts[i] || '';
        if (text) {
            const minX = Math.min(...poly.map(p => p[0])) * scale;
            const minY = Math.min(...poly.map(p => p[1])) * scale;
            ctx.font = '11px -apple-system, sans-serif';
            const tw = ctx.measureText(text).width + 10;
            const ly = minY - 20;
            ctx.fillStyle = isSelected ? SELECTED_BORDER : HOVER_BORDER;
            ctx.beginPath();
            ctx.roundRect(minX, Math.max(0, ly), tw, 18, 3);
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.fillText(text, minX + 5, Math.max(0, ly) + 13);
        }
        ctx.restore();
    }
}

function PageCanvas({ sourceFile, pageIdx, pageData, containerWidth, hoveredBlockIdx, selectedBlockIdx, onBlockHover, onBlockClick, onVisible }) {
    const wrapRef = useRef(null);
    const imgCanvasRef = useRef(null);
    const overlayRef = useRef(null);
    const imgRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [imgUrl, setImgUrl] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [scale, setScale] = useState(1);

    const blocks = pageData?.blocks || [];
    const spottingRes = pageData?.spottingRes;
    const spottingPolys = spottingRes?.rec_polys || [];
    const isSpotting = spottingPolys.length > 0;
    const pw = pageData?.pageWidth;
    const ph = pageData?.pageHeight;

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
        }, { rootMargin: '200px' });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (!visible || !sourceFile) return;
        const isPdf = sourceFile.name?.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            setImgUrl(URL.createObjectURL(sourceFile));
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const pdfjsLib = await import('pdfjs-dist');
                pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
                const buf = await sourceFile.arrayBuffer();
                const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
                if (pageIdx >= pdf.numPages) return;
                const page = await pdf.getPage(pageIdx + 1);
                const vp = page.getViewport({ scale: 2 });
                const canvas = document.createElement('canvas');
                canvas.width = vp.width; canvas.height = vp.height;
                await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
                if (!cancelled) setImgUrl(canvas.toDataURL());
            } catch (e) { console.error('PDF page render error:', e); }
        })();
        return () => { cancelled = true; };
    }, [visible, sourceFile, pageIdx]);

    useEffect(() => {
        if (!imgUrl) return;
        const img = new Image();
        img.onload = () => { imgRef.current = img; setLoaded(true); };
        img.src = imgUrl;
    }, [imgUrl]);

    useEffect(() => {
        if (!loaded || !containerWidth) return;
        const naturalW = pw || imgRef.current?.naturalWidth || 1;
        setScale(containerWidth / naturalW);
    }, [loaded, containerWidth, pw]);

    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;

    useEffect(() => {
        if (!loaded || !imgCanvasRef.current || !imgRef.current) return;
        const naturalW = pw || imgRef.current.naturalWidth;
        const naturalH = ph || imgRef.current.naturalHeight;
        const dw = Math.round(naturalW * scale);
        const dh = Math.round(naturalH * scale);
        imgCanvasRef.current.width = dw * dpr;
        imgCanvasRef.current.height = dh * dpr;
        const ctx = imgCanvasRef.current.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.drawImage(imgRef.current, 0, 0, dw, dh);
    }, [loaded, scale, pw, ph, dpr]);

    useEffect(() => {
        if (!loaded || !overlayRef.current) return;
        const naturalW = pw || imgRef.current?.naturalWidth || 1;
        const naturalH = ph || imgRef.current?.naturalHeight || 1;
        const ow = Math.round(naturalW * scale);
        const oh = Math.round(naturalH * scale);
        overlayRef.current.width = ow * dpr;
        overlayRef.current.height = oh * dpr;
        const ctx = overlayRef.current.getContext('2d');
        ctx.scale(dpr, dpr);
        if (isSpotting) {
            drawSpottingOnSource(ctx, spottingPolys, spottingRes?.rec_texts || [], scale, hoveredBlockIdx, selectedBlockIdx);
        } else {
            drawOverlay(ctx, blocks, scale, hoveredBlockIdx, selectedBlockIdx);
        }
    }, [loaded, blocks, scale, hoveredBlockIdx, selectedBlockIdx, pw, ph, isSpotting, spottingPolys, spottingRes, dpr]);

    const hitTest = useCallback((e) => {
        if (!overlayRef.current) return -1;
        const rect = overlayRef.current.getBoundingClientRect();
        const mx = e.clientX - rect.left, my = e.clientY - rect.top;
        if (isSpotting) {
            for (let i = spottingPolys.length - 1; i >= 0; i--) {
                const p = spottingPolys[i];
                if (!p || p.length < 4) continue;
                const minX = Math.min(...p.map(pt => pt[0])) * scale;
                const maxX = Math.max(...p.map(pt => pt[0])) * scale;
                const minY = Math.min(...p.map(pt => pt[1])) * scale;
                const maxY = Math.max(...p.map(pt => pt[1])) * scale;
                if (mx >= minX && mx <= maxX && my >= minY && my <= maxY) return i;
            }
            return -1;
        }
        for (let i = blocks.length - 1; i >= 0; i--) {
            const b = getBlockBounds(blocks[i]);
            if (!b) continue;
            if (mx >= b.x * scale && mx <= (b.x + b.w) * scale && my >= b.y * scale && my <= (b.y + b.h) * scale) return i;
        }
        return -1;
    }, [blocks, scale, isSpotting, spottingPolys]);

    const estHeight = loaded
        ? Math.round((ph || imgRef.current?.naturalHeight || 800) * scale)
        : Math.round((ph || 800) * (containerWidth / (pw || 600)));

    return (
        <div ref={wrapRef} style={{ minHeight: visible && !loaded ? estHeight : 'auto', position: 'relative' }}>
            {visible && !loaded && (
                <div className="flex items-center justify-center" style={{ height: estHeight, color: '#aeb4c9', fontSize: '12px' }}>
                    Loading page {pageIdx + 1}...
                </div>
            )}
            {loaded && (
                <div className="relative inline-block" style={{ width: '100%' }}>
                    <canvas ref={imgCanvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
                    <canvas
                        ref={overlayRef}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                        onMouseMove={e => { const idx = hitTest(e); onBlockHover?.(pageIdx, idx >= 0 ? idx : null); }}
                        onClick={e => { const idx = hitTest(e); onBlockClick?.(pageIdx, idx >= 0 ? idx : null); }}
                        onMouseLeave={() => onBlockHover?.(pageIdx, null)}
                    />
                    {hoveredBlockIdx != null && hoveredBlockIdx >= 0 && blocks[hoveredBlockIdx] && (() => {
                        const block = blocks[hoveredBlockIdx];
                        const bounds = getBlockBounds(block);
                        if (!bounds || !block.block_content) return null;
                        return (
                            <button
                                className="absolute z-10 flex items-center gap-1 px-2 py-1 bg-white rounded-md shadow-md text-gray-600 hover:bg-gray-50"
                                style={{ left: bounds.x * scale, top: (bounds.y + bounds.h) * scale + 4, fontSize: '11px', border: '1px solid #e1e6f8' }}
                                onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(block.block_content || ''); }}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                Copy
                            </button>
                        );
                    })()}
                </div>
            )}
            {loaded && (
                <div className="flex items-center gap-3 px-4 py-1">
                    <div className="flex-1 h-px" style={{ background: '#e1e6f8' }} />
                    <span style={{ fontSize: '10px', color: '#aeb4c9' }}>Page {pageIdx + 1}</span>
                    <div className="flex-1 h-px" style={{ background: '#e1e6f8' }} />
                </div>
            )}
        </div>
    );
}

export default function OcrSourcePreview({
    sourceFile,
    pages,
    activePage,
    hoveredBlockIdx,
    selectedBlockIdx,
    onBlockHover,
    onBlockClick,
    scrollRef,
    onScroll,
}) {
    const internalRef = useRef(null);
    const containerRef = scrollRef || internalRef;
    const [containerWidth, setContainerWidth] = useState(0);
    const pageRefs = useRef({});

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (activePage != null && pageRefs.current[activePage]) {
            pageRefs.current[activePage].scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [activePage]);

    if (!sourceFile) {
        return (
            <div className="flex items-center justify-center h-full" style={{ color: '#aeb4c9', fontSize: '13px' }}>
                Upload file to preview source
            </div>
        );
    }

    const totalPages = pages?.length || 0;

    return (
        <div ref={containerRef} className="flex-1 overflow-y-auto" style={{ background: '#f7f9ff' }} onScroll={onScroll}>
            {totalPages > 0 ? (
                (pages || []).map((page, i) => (
                    <div key={i} ref={el => pageRefs.current[i] = el}>
                        <PageCanvas
                            sourceFile={sourceFile}
                            pageIdx={i}
                            pageData={page}
                            containerWidth={containerWidth}
                            hoveredBlockIdx={activePage === i ? hoveredBlockIdx : null}
                            selectedBlockIdx={activePage === i ? selectedBlockIdx : null}
                            onBlockHover={onBlockHover}
                            onBlockClick={onBlockClick}
                        />
                    </div>
                ))
            ) : (
                <PageCanvas
                    sourceFile={sourceFile}
                    pageIdx={0}
                    pageData={null}
                    containerWidth={containerWidth}
                    hoveredBlockIdx={null}
                    selectedBlockIdx={null}
                    onBlockHover={onBlockHover}
                    onBlockClick={onBlockClick}
                />
            )}
        </div>
    );
}
